import { Icon } from '@iconify/react'
import axios from 'axios'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { logout } from '../redux/slices/authenticate'
import { toogle } from '../redux/slices/sidebar'
import { getToken, toastError } from '../utils/helpers/methods'
import IconMD from "../images/icon.png";
import { AiOutlineClose } from 'react-icons/ai';
import '../App.css'
import Avatar from './shared/chat/avatar'

const Sidebar = () => {
  const { userToken, userInfo, doctorDetails } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { open } = useSelector(state => state.sidebar);
  const [inputs, setInputs] = useState({ credentialsInvalid: false });
  const { docinfo } = useSelector((state) => state.pass);
  const [firstName, setFirstName] = useState('');
  const [secondName, setSecondName] = useState('');

  useEffect(() => {
    if (docinfo) {
      const [firstLetter, secondletter] = docinfo?.name?.split(" ").map((part) => part.charAt(0).toUpperCase());
      setFirstName(firstLetter)
      setSecondName(secondletter)
    }
  }, [docinfo])

  useEffect(() => { getToken(); }, []);
  useEffect(() => {
    if (inputs.credentialsInvalid) {
      localStorage.clear();
      navigate('/login');
      toastError("Unauthorized access. Please log in or check your credentials and try again. logging out...");
      dispatch(logout());
    }
  }, [inputs]);

  const handleToggle = () => dispatch(toogle());

  axios.interceptors.response.use(
    response => response,
    error => {
      if (error?.response?.status === 401) {
        setInputs({ ...inputs, credentialsInvalid: true });
      }
      return Promise.reject(error);
    }
  );

  // TODO: wire these counts to real redux state (unread chats, urgent requests, etc.)
  const chatCount = useSelector(state => state.chat?.unreadCount) ?? 0;
  const urgentCount = useSelector(state => state.urgent?.count) ?? 0;

  const sections = [
    {
      label: 'WORKSPACE',
      items: [
        { path: '/dashboard', text: 'Dashboard', icon: 'tabler:layout-grid' },
        { path: '/patientList', text: 'Patients', icon: 'tabler:users' },
        { path: '/chat', text: 'Chat', icon: 'tabler:message-circle', badge: chatCount?chatCount:null },
        { path: '/urgentList', text: 'Urgent Requests', icon: 'tabler:alert-triangle', badge: urgentCount?urgentCount:null, badgeVariant: 'warning' },
      ],
    },
    {
      label: 'TOOLS',
      items: [
        { path: '/predefined', text: 'Predefined Message', icon: 'tabler:file-text' },
        // { path: '/reports', text: 'Reports', icon: 'tabler:chart-bar' },
        { path: '/profile', text: 'Profile', icon: 'tabler:user-circle', badge: 'You', badgeVariant: 'active' },
        { path: '/settings', text: 'Settings', icon: 'tabler:settings' },
      ],
    },
  ];

  const initials = (userInfo?.name || 'Dr Pal')
    .split(' ')
    .map(w => w[0])
    .join('')
    .toUpperCase();

  return (
    <>
      {open && (
        <div
          className="lg:hidden fixed w-full h-full bg-black opacity-50"
          style={{ zIndex: 1024 }}
          onClick={() => dispatch(toogle())}
        />
      )}
      <div
        className={`${open ? 'w-[280px]' : 'w-0'} sidebar-content bg-[#1a4228] fixed lg:relative left-0 top-0 bottom-0 overflow-hidden flex flex-col transition-all duration-200`}
        style={{ position: 'fixed', zIndex: 1048 }}
      >
        {/* Logo header */}
        <div className="flex items-center justify-between px-4 py-4 shrink-0">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate('/dashboard')}
          >
            <img src={IconMD} className="w-8 h-8" alt="mymd" />
            <div>
              <h1 className="text-white font-bold text-[17px] leading-tight">Care Web</h1>
              <p className="text-white/50 text-[11px] leading-tight">MyMD Connect</p>
            </div>
          </div>
          <AiOutlineClose
            size={20}
            className="lg:hidden cursor-pointer text-white/70"
            onClick={handleToggle}
          />
        </div>

        {/* Nav sections */}
        <div className="flex-1 overflow-y-auto px-3 pb-3">
          {sections.map((section) => (
            <div key={section.label} className="mb-5">
              <p className="text-white/40 text-[11px] font-semibold tracking-wider px-3 mb-2">
                {section.label}
              </p>
              <div className="flex flex-col gap-1">
                {section.items.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link to={item.path} key={item.path}>
                      <div
                        className={`flex items-center justify-between px-3 py-2 rounded-lg text-[14px] transition-colors
                          ${isActive
                            ? 'bg-white/10 text-white font-medium'
                            : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon icon={item.icon} width={18} height={18} />
                          <span>{item.text}</span>
                        </div>
                        {item.badge != null && (
                          <span
                            className={`text-[11px] font-semibold rounded-full px-2 py-[1px]
                              ${item.badgeVariant === 'active'
                                ? 'bg-green-500/20 text-green-400'
                                : item.badgeVariant === 'warning'
                                ? 'bg-red-500 text-white'
                                : 'bg-red-500 text-white'}`}
                          >
                            {item.badge}
                          </span>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom user card */}
        <div className="shrink-0 border-t border-white/10 p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            {doctorDetails?.data?.profile_image ? (
              <Avatar
                image={doctorDetails?.data?.profile_image}
                width={"w-9 h-9"}
                profile={true}
              />) : (
              <div className="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center text-white text-[13px] font-semibold shrink-0">
                {initials}
              </div>
            )}
            
            <div className="min-w-0">
              <p className="text-white text-[13px] font-medium truncate">
                {userInfo?.name}
              </p>
              <p className="text-white/40 text-[11px] truncate">
                Practitioner ID -{userInfo?.doctor_login.practitioner_id ? `${userInfo.doctor_login.practitioner_id}` : ''}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;